// frontend/src/components/DashboardReorder.js
//
// Wrapper voor de widgets-grid die drag-and-drop toevoegt zonder dat
// Dashboard.js herschreven hoeft te worden.
//
// Hoe het werkt:
//   - Het component krijgt de children (alle widgets) en een `reorderEnabled` flag
//   - Elk child MOET een `data-widget-key="xxx"` attribuut hebben op het
//     buitenste element (of daarbinnen direct herkenbaar)
//   - Bij enabled=false: children worden gewoon in meegegeven volgorde getoond
//   - Bij enabled=true: rendert widgets met drag handles + opslaan via API
//
// Usage in Dashboard.js:
//   <DashboardReorder>
//     <div data-widget-key="openstaande">...</div>
//     <div data-widget-key="behandeling">...</div>
//     ...
//   </DashboardReorder>

import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useLayoutEdit } from '../context/LayoutEditContext';

const DashboardReorder = ({ children, className = 'widgets-grid' }) => {
  // editMode en saving komen uit globale context
  const { editMode, saving, startEdit, cancelEdit, registerSaver } = useLayoutEdit();
  const [savedOrder, setSavedOrder] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const originalOrderRef = useRef(null);

  // Haal opgeslagen volgorde op bij mount
  useEffect(() => {
    let alive = true;
    api.get('/dashboard/volgorde')
      .then(res => { if (alive) setSavedOrder(res.data.volgorde); })
      .catch(() => { if (alive) setSavedOrder(null); });
    return () => { alive = false; };
  }, []);

  // Zet children om naar array met keys
  const childArray = React.Children.toArray(children).filter(c => {
    if (!React.isValidElement(c)) return false;
    const key = c.props?.['data-widget-key'];
    return typeof key === 'string' && key.length > 0;
  });

  const allKeys = childArray.map(c => c.props['data-widget-key']);

  // Bepaal effective volgorde:
  // 1. currentOrder (tijdens edit)
  // 2. savedOrder (opgeslagen)
  // 3. allKeys (default, in volgorde van JSX)
  const effectiveOrder = (() => {
    const base = currentOrder || savedOrder;
    if (!base) return allKeys;

    // Behoud bekende keys in opgeslagen volgorde, voeg nieuwe/onbekende achteraan toe
    const known = base.filter(k => allKeys.includes(k));
    const extras = allKeys.filter(k => !known.includes(k));
    return [...known, ...extras];
  })();

  // Map: key → child element
  const childMap = {};
  childArray.forEach(c => {
    childMap[c.props['data-widget-key']] = c;
  });

  useEffect(() => {
    if (editMode) {
      originalOrderRef.current = effectiveOrder.slice();
      setCurrentOrder(effectiveOrder.slice());
    } else {
      setCurrentOrder(null);
      setDragKey(null);
      setHoverKey(null);
    }
  }, [editMode]);

  useEffect(() => {
    const unregister = registerSaver('dashboard', {
      onSave: async () => {
        const res = await api.put('/dashboard/volgorde', { volgorde: effectiveOrder });
        setSavedOrder(res.data.volgorde);
      },
      onCancel: () => {
        setCurrentOrder(null);
        setDragKey(null);
        setHoverKey(null);
      }
    });
    return unregister;
  }, [effectiveOrder, registerSaver]);

  const handleDragStart = useCallback((key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox vereist data
    try { e.dataTransfer.setData('text/plain', key); } catch (_) {}
  }, []);

  const handleDragOver = useCallback((key) => (e) => {
    if (!dragKey || dragKey === key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverKey !== key) setHoverKey(key);
  }, [dragKey, hoverKey]);

  const handleDrop = useCallback((targetKey) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey) return;

    setCurrentOrder(prev => {
      const base = prev || effectiveOrder;
      const next = base.filter(k => k !== dragKey);
      const tgtIdx = next.indexOf(targetKey);
      if (tgtIdx === -1) return base;
      next.splice(tgtIdx, 0, dragKey);
      return next;
    });
    setDragKey(null);
    setHoverKey(null);
  }, [dragKey, effectiveOrder]);

  const handleDragEnd = useCallback(() => {
    setDragKey(null);
    setHoverKey(null);
  }, []);

  // Mobile touch support - simpele swap via up/down knoppen
  const moveUp = (key) => {
    setCurrentOrder(prev => {
      const base = prev || effectiveOrder.slice();
      const idx = base.indexOf(key);
      if (idx <= 0) return base;
      const next = base.slice();
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (key) => {
    setCurrentOrder(prev => {
      const base = prev || effectiveOrder.slice();
      const idx = base.indexOf(key);
      if (idx === -1 || idx >= base.length - 1) return base;
      const next = base.slice();
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  // Als niet in edit mode: render normaal in opgeslagen volgorde
  // (edit knop zit in sidebar, globaal)
  if (!editMode) {
    return (
      <div className={className}>
        {effectiveOrder.map(k => (
          <React.Fragment key={k}>{childMap[k]}</React.Fragment>
        ))}
      </div>
    );
  }

  // Edit mode: widgets met drag handles + controls
  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        padding: '10px 14px',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 8,
        color: '#1e40af',
        fontSize: 13,
      }}>
        <span>
          <strong>Layout bewerken:</strong> sleep widgets om ze te verplaatsen, of gebruik de ↑↓ knoppen. Klik <strong>Opslaan</strong> linksonder in de sidebar.
        </span>
      </div>

      <div className={className}>
        {effectiveOrder.map((k, idx) => {
          const child = childMap[k];
          const isDragging = dragKey === k;
          const isHovering = hoverKey === k && dragKey !== k;

          return (
            <div
              key={k}
              draggable
              onDragStart={handleDragStart(k)}
              onDragOver={handleDragOver(k)}
              onDrop={handleDrop(k)}
              onDragEnd={handleDragEnd}
              style={{
                position: 'relative',
                cursor: 'grab',
                opacity: isDragging ? 0.4 : 1,
                outline: isHovering ? '2px dashed #3b82f6' : 'none',
                outlineOffset: 2,
                borderRadius: 8,
                transition: 'opacity 0.15s',
              }}
            >
              {/* Overlay badge met up/down knoppen en grip */}
              <div style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                display: 'flex',
                gap: 4,
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 6,
                padding: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <button
                  onClick={(e) => { e.stopPropagation(); moveUp(k); }}
                  disabled={idx === 0}
                  style={{
                    width: 24, height: 24, padding: 0,
                    background: idx === 0 ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: idx === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                  }}
                  title="Eerder"
                >↑</button>
                <button
                  onClick={(e) => { e.stopPropagation(); moveDown(k); }}
                  disabled={idx === effectiveOrder.length - 1}
                  style={{
                    width: 24, height: 24, padding: 0,
                    background: idx === effectiveOrder.length - 1 ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: idx === effectiveOrder.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                  }}
                  title="Later"
                >↓</button>
              </div>

              {/* Grip indicator linksboven */}
              <div style={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 10,
                fontSize: 10,
                color: '#94a3b8',
                background: 'rgba(255,255,255,0.9)',
                padding: '2px 6px',
                borderRadius: 4,
                userSelect: 'none',
                pointerEvents: 'none',
              }}>
                ⋮⋮ sleep
              </div>

              {child}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default DashboardReorder;
